import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Typography,
  Row,
  Col,
  Button,
  Spin,
  message,
  Statistic,
  Space,
  Tooltip,
  Empty,
  Modal,
  Checkbox
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  PrinterOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import CustomDatePicker from '../components/CustomDatePicker';
import { getAttendanceByDate } from '../services/attendanceService';
import { themeConfig } from '../config/theme';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

const AttendanceByDate = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [isPrintModalVisible, setIsPrintModalVisible] = useState(false);

  useEffect(() => {
    // Load today's attendance by default
    fetchAttendanceByDate(dayjs().format('YYYY-MM-DD'));
  }, []);

  const fetchAttendanceByDate = async (date) => {
    setLoading(true);
    try {
      const response = await getAttendanceByDate(date);
      setAttendanceData(response);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch attendance data';
      messageApi.error(errorMessage);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
      fetchAttendanceByDate(date.format('YYYY-MM-DD'));
    }
  };

  const handleRefresh = () => {
    fetchAttendanceByDate(selectedDate.format('YYYY-MM-DD'));
  };

  const handlePrint = () => {
    if (!attendanceData || !attendanceData.data || attendanceData.data.length === 0) {
      messageApi.warn('Please load attendance data first.');
      return;
    }
    setIsPrintModalVisible(true);
  };

  const handlePrintContent = () => {
    const printContent = document.getElementById('printable-attendance-date-content');
    const originalContents = document.body.innerHTML;
    
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    
    // Re-render the component after printing
    window.location.reload();
  };

  const handleDownloadCSV = () => {
    if (!attendanceData || !attendanceData.data) return;

    const headers = [
      'Employee Name',
      'Employment Type',
      'Status',
      'Site',
      'Marked By'
    ];

    const csvContent = [
      headers.join(','),
      ...attendanceData.data.map(record => [
        `"${record.fullName || ''} (${record.job || 'N/A'})"`,
        `"${record.employmentType || ''}"`,
        `"${record.displayStatus || ''}"`,
        `"${record.siteName || 'Not Assigned'}"`,
        `"${record.markedByName || 'Not Marked'}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `attendance_${selectedDate.format('DD_MMM_YYYY')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusColor = (status, isFixedUser) => {
    if (!status || status === 'Not Marked') return 'default';
    
    // Simplified color coding: only Green, Red, and Gray
    if (status === 'A' || status === 'H') {
      return 'red'; // Absent or Holiday
    } else {
      return 'green'; // Any other status (Present, Half Day, Leave) is considered marked
    }
  };

  const columns = [
    {
      title: 'Employee',
      key: 'employee',
      fixed: 'left',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: '500' }}>{record.fullName}</div>
          <div style={{ fontSize: '12px', color: themeConfig.text.muted }}>
            {record.job} • {record.employmentType}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'displayStatus',
      key: 'status',
      width: 120,
      render: (status, record) => {
        console.log(status);
        if (status === 'Not Marked') {
          return <Tag color="default">Not Marked</Tag>;
        }

        if (record.isFixedUser && status !== 'A' && status !== 'H') {
          // For FIXED users, show Present with original status in brackets (except for A and H)
          
          return (
            <Tooltip title={`Marked by supervisor: ${record.originalStatus}`}>
              <Tag color="green">
                {record.displayStatus} (Marked as: {record.originalStatus})
              </Tag>
            </Tooltip>
          );
        }

        if (status === 'H') {
          return (
            <Tooltip title={`Marked by supervisor: ${record.originalStatus}`}>
              <Tag color="red">
                H ({record.narration})
              </Tag>
            </Tooltip>
          );
        }

        return (
          <Tag color={getStatusColor(record.originalStatus, false)}>
            {record.displayStatus}
          </Tag>
        );
      },
      filters: [
        { text: 'Marked', value: 'MARKED' },
        { text: 'Absent', value: 'A' },
        { text: 'Holiday', value: 'H' },
        { text: 'Not Marked', value: 'Not Marked' },
      ],
      onFilter: (value, record) => {
        if (value === 'Not Marked') {
          return record.displayStatus === 'Not Marked';
        } else if (value === 'MARKED') {
          return record.originalStatus && record.originalStatus !== 'A' && record.originalStatus !== 'H';
        } else if (value === 'H') {
          return record.originalStatus === 'H';
        }
        return record.originalStatus === value;
      },
    },
    {
      title: 'Site',
      dataIndex: 'siteName',
      key: 'site',
      width: 120,
      render: (siteName, record) => {
        if (!siteName) {
          return <Tag color="default">Not Assigned</Tag>;
        }
        return (
          <Tooltip title={`Site ID: ${record.siteId}`}>
            <Tag color="blue">{siteName}</Tag>
          </Tooltip>
        );
      },
      filters: attendanceData?.data
        ? [...new Set(attendanceData.data
            .map(item => item.siteName)
            .filter(Boolean))]
            .map(site => ({ text: site, value: site }))
        : [],
      onFilter: (value, record) => record.siteName === value,
    },
    {
      title: 'Employment Type',
      dataIndex: 'employmentType',
      key: 'employmentType',
      width: 120,
      render: (type) => (
        <Tag color={type === 'FIXED' ? 'purple' : 'orange'}>
          {type}
        </Tag>
      ),
      filters: [
        { text: 'FIXED', value: 'FIXED' },
        { text: 'SHIFT', value: 'SHIFT' },
      ],
      onFilter: (value, record) => record.employmentType === value,
    },
    // Append plain-text Marked By column at the end
    {
      title: 'Marked By',
      key: 'markedByText',
      width: 220,
      render: (_, record) => {
        if (!record.hasAttendance) return 'Not Marked';
        return record.markedByName || 'Unknown';
      },
      sorter: (a, b) => (a.markedByName || '').localeCompare(b.markedByName || ''),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        // Get unique marked by names from current data
        const uniqueMarkedBy = [...new Set(attendanceData?.data?.map(record => {
          if (!record.hasAttendance) return 'Not Marked';
          return record.markedByName || 'Unknown';
        }).filter(name => name))].sort();
        
        return (
          <div style={{ padding: 8, maxHeight: 300, overflowY: 'auto' }}>
            <div style={{ marginBottom: 8 }}>
              <Checkbox
                indeterminate={selectedKeys.length > 0 && selectedKeys.length < uniqueMarkedBy.length}
                checked={selectedKeys.length === uniqueMarkedBy.length}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedKeys(uniqueMarkedBy);
                  } else {
                    setSelectedKeys([]);
                  }
                }}
              >
                Select All
              </Checkbox>
            </div>
            <div style={{ borderTop: `1px solid ${themeConfig.border.light}`, paddingTop: 8 }}>
              {uniqueMarkedBy.map(name => (
                <div key={name} style={{ marginBottom: 4 }}>
                  <Checkbox
                    checked={selectedKeys.includes(name)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedKeys([...selectedKeys, name]);
                      } else {
                        setSelectedKeys(selectedKeys.filter(key => key !== name));
                      }
                    }}
                  >
                    {name}
                  </Checkbox>
                </div>
              ))}
            </div>
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8, marginTop: 8 }}>
              <Space>
                <Button
                  type="primary"
                  onClick={() => confirm()}
                  size="small"
                  style={{ width: 90 }}
                >
                  OK
                </Button>
                <Button
                  onClick={() => clearFilters()}
                  size="small"
                  style={{ width: 90 }}
                >
                  Reset
                </Button>
              </Space>
            </div>
          </div>
        );
      },
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? themeConfig.status.info : undefined }} />,
      onFilter: (value, record) => {
        // For checkbox filters, value is the array of selected keys
        if (!value || value.length === 0) return true;
        const recordMarkedBy = !record.hasAttendance ? 'Not Marked' : (record.markedByName || 'Unknown');
        return value.includes(recordMarkedBy);
      },
    },
  ];

  const getSummaryStats = () => {
    if (!attendanceData || !attendanceData.data) return null;

    const { data, totalUsers, usersWithAttendance, usersWithoutAttendance } = attendanceData;
    
    const markedCount = data.filter(u => u.originalStatus && u.originalStatus !== 'A' && u.originalStatus !== 'H').length;
    const absentCount = data.filter(u => u.originalStatus === 'A' || u.originalStatus === 'H').length;

    return {
      totalUsers,
      usersWithAttendance,
      usersWithoutAttendance,
      markedCount,
      absentCount
    };
  };

  const stats = getSummaryStats();

  return (
    <div style={{ padding: '24px' }}>
      {contextHolder}
      
      <Title level={2}>
        <CalendarOutlined style={{ marginRight: '8px' }} />
        Attendance by Date
      </Title>

      <Paragraph>
        View attendance records for all workers and supervisors on a specific date. 
        FIXED employment type users are always shown as Present, with their original marked status in brackets.
      </Paragraph>

      {/* Date Selection and Controls */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col>
            <Text strong>Select Date:</Text>
          </Col>
          <Col>
            <CustomDatePicker
              value={selectedDate}
              onChange={handleDateChange}
              format="DD MMM YYYY"
              allowClear={false}
              style={{ width: 150 }}
              disabledDate={(current) => current && current > dayjs().endOf('day')}
              defaultValue={dayjs()}
              showToday={true}
              placeholder="Select Date"
              // Additional props to prevent year jumping
              inputReadOnly={false}
              autoFocus={false}
            />
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => fetchAttendanceByDate(selectedDate.format('YYYY-MM-DD'))}
              loading={loading}
            >
              View Attendance
            </Button>
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>
          </Col>
          <Col>
            <Button
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              disabled={!attendanceData || !attendanceData.data || attendanceData.data.length === 0}
            >
              Print Attendance
            </Button>
          </Col>
          <Col>
            <Text type="secondary">
              Showing: {selectedDate.format('DD MMMM YYYY')}
            </Text>
          </Col>
        </Row>
      </Card>

      {/* Summary Statistics */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Users"
                value={stats.totalUsers}
                valueStyle={{ color: themeConfig.status.info }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Marked"
                value={stats.markedCount}
                valueStyle={{ color: themeConfig.status.success }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Absent"
                value={stats.absentCount}
                valueStyle={{ color: themeConfig.status.error }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Not Marked"
                value={stats.usersWithoutAttendance}
                valueStyle={{ color: themeConfig.text.mutedMedium }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Attendance Table */}
      <Card
        title={
          <Space>
            <CalendarOutlined />
            Attendance Records
            {attendanceData && (
              <Text type="secondary">
                ({attendanceData.data?.length || 0} users)
              </Text>
            )}
          </Space>
        }
        extra={
          <Space>
            <Text type="secondary">
              Last updated: {dayjs().format('HH:mm:ss')}
            </Text>
          </Space>
        }
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>Loading attendance data...</div>
          </div>
        ) : attendanceData?.data?.length > 0 ? (
          <Table
            columns={columns}
            dataSource={attendanceData.data}
            rowKey="mobileNumber"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} users`,
            }}
            scroll={{ x: 800 }}
            size="middle"
          />
        ) : (
          <Empty
            description="No attendance data found for the selected date"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>

      {/* Information Panel */}
      <Card
        title={
          <Space>
            <InfoCircleOutlined />
            Information
          </Space>
        }
        style={{ marginTop: '24px' }}
        size="small"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div>
              <Text strong>FIXED Employment Type:</Text>
              <br />
              <Text type="secondary">
                Users with FIXED employment type are always shown as "Present" in the main status column, 
                with their original marked status displayed in brackets. This is because FIXED users 
                are considered present regardless of their actual attendance marking.
              </Text>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div>
              <Text strong>Status Colors:</Text>
              <br />
              <Text type="secondary">
                <Tag color="green">Green</Tag> = Marked (Present, Half Day, Leave), 
                <Tag color="red">Red</Tag> = Absent, 
                <Tag color="default">Gray</Tag> = Not Marked
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Print Modal */}
      <Modal
        title="Print Attendance Data"
        visible={isPrintModalVisible}
        onCancel={() => setIsPrintModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsPrintModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownloadCSV}>
            Download CSV
          </Button>,
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrintContent}>
            Print
          </Button>
        ]}
        width={1000}
        destroyOnClose
      >
        <div id="printable-attendance-date-content">
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <h2>Daily Attendance Report</h2>
            <p><strong>Date:</strong> {selectedDate.format('DD MMMM YYYY')}</p>
            <p><strong>Generated on:</strong> {new Date().toLocaleDateString()}</p>
          </div>
          
          <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${themeConfig.border.light}` }}>
            <thead>
              <tr style={{ backgroundColor: themeConfig.background.tableHeader }}>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Employee Name</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Employment Type</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Status</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Site</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Marked By</th>
              </tr>
            </thead>
            <tbody>
              {attendanceData?.data?.map((record, index) => (
                <tr key={record.mobileNumber} style={{ backgroundColor: index % 2 === 0 ? themeConfig.background.tableRowOdd : themeConfig.background.tableRowEven }}>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{record.fullName || 'N/A'} ({record.job || 'N/A'})</td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{record.employmentType || 'N/A'}</td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>
                    <span style={{ 
                      color: (record.originalStatus === 'A' || record.originalStatus === 'H') ? '#ff4d4f' : 
                             record.originalStatus ? '#52c41a' : '#999'
                    }}>
                      {record.displayStatus || 'Not Marked'}
                    </span>
                  </td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{record.siteName || 'Not Assigned'}</td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{record.markedByName || 'Not Marked'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          
          <div style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <div>
                <p><strong>Total Users:</strong> {attendanceData?.totalUsers || 0}</p>
                <p><strong>Marked:</strong> {attendanceData?.data?.filter(r => r.originalStatus && r.originalStatus !== 'A' && r.originalStatus !== 'H').length || 0}</p>
                <p><strong>Absent:</strong> {attendanceData?.data?.filter(r => r.originalStatus === 'A' || r.originalStatus === 'H').length || 0}</p>
              </div>
              <div>
                <p><strong>Not Marked:</strong> {attendanceData?.usersWithoutAttendance || 0}</p>
                <p><strong>FIXED Users:</strong> {attendanceData?.data?.filter(r => r.employmentType === 'FIXED').length || 0}</p>
                <p><strong>SHIFT Users:</strong> {attendanceData?.data?.filter(r => r.employmentType === 'SHIFT').length || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AttendanceByDate;
